import { Text, Stack, TextInput, TextInputProps, TextProps } from '@mantine/core';
import { PhoneEditableField } from './PhoneEditableField';
import { EmailEditableField } from './EmailEditableField';
import { EditableField, EditableFieldRenderInputProps } from './EditableField';
import { inferSystem } from '@/services/organization';
import { checkEmailValidation, checkPhoneValidation, normalizeEmail } from './services';
import { formatPhoneNumber, normalizePhoneNumber } from '@bariendo/lib/phone-validations';
import { useCallback } from 'react';

export interface SmartEditableFieldProps {
  value: string;
  displayValue?: string;
  placeholder?: string;
  onSubmit: (newValue: string) => Promise<void> | void;
  onChange?: (newValue: string) => void;
  onCancel?: () => void;
  onEditStart?: () => void;
  onEndEditing?: (info: { value: string; submitted: boolean; canceled: boolean }) => void;
  editable?: boolean;
  textProps?: TextProps;
  inputProps?: TextInputProps;
  requireNonEmpty?: boolean;
  focus?: boolean;
  defaultTo?: 'phone' | 'email' | 'text';
  dynamicSystem?: boolean;
}

/**
 * SmartEditableField that automatically detects the input type
 * and uses the appropriate specialized component (Phone, Email, etc.)
 *
 * Note: Detection happens on the initial value, not dynamically during editing
 * to avoid component swapping issues
 */
export function SmartEditableField(props: SmartEditableFieldProps) {
  const { value, dynamicSystem = false } = props;

  // Dynamic mode: keep a single EditableField mounted and adapt behavior as user types
  const displayValue = (() => {
    const sys = inferSystem(value);
    if (sys === 'phone') return formatPhoneNumber(value);
    return props.displayValue;
  })();

  const DynamicInput = useCallback(
    (p: EditableFieldRenderInputProps) => {
      const sys = inferSystem(p.value);
      const isPhone = sys === 'phone';
      const isEmail = sys === 'email';
      const placeholder = isPhone ? '+1 (555) 123-4567' : isEmail ? 'email@example.com' : props.placeholder;

      const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => p.onChange(e.target.value);

      // Merge styles from inputProps with validation styles
      const mergedStyles = {
        ...p.inputProps?.styles,
        input: {
          ...(p.inputProps?.styles as any)?.input,
          borderColor: p.validationError
            ? 'var(--mantine-color-red-6)'
            : (p.inputProps?.styles as any)?.input?.borderColor,
        },
      };

      return (
        <Stack gap={2} style={{ width: '100%' }}>
          <TextInput
            value={p.value}
            onChange={handleChange}
            onBlur={p.onBlur}
            onKeyDown={p.onKeyDown}
            disabled={p.disabled}
            ref={p.inputRef}
            autoFocus
            placeholder={placeholder}
            {...p.inputProps}
            styles={mergedStyles}
            error={!!p.validationError}
          />
          {p.validationError && (
            <Text size="xs" c="red" style={{ fontSize: '10px' }}>
              {p.validationError}
            </Text>
          )}
        </Stack>
      );
    },
    [props.placeholder]
  );

  const liveValidate = (v: string) => {
    const sys = inferSystem(v);
    if (sys === 'phone') return checkPhoneValidation(v);
    if (sys === 'email') return checkEmailValidation(v);
    return undefined;
  };

  const normalizeValue = (v: string) => {
    const sys = inferSystem(v);
    if (sys === 'phone') return normalizePhoneNumber(v);
    if (sys === 'email') return normalizeEmail(v);
    return v.trim();
  };

  // If not dynamic, preserve previous behavior (decide once on initial value)
  if (!dynamicSystem) {
    const inferredType = inferSystem(value);
    const type = inferredType === 'phone' || inferredType === 'email' ? inferredType : props.defaultTo;

    switch (type) {
      case 'phone':
        return <PhoneEditableField {...props} />;
      case 'email':
        return <EmailEditableField {...props} />;
      default:
        return <EditableField {...props} />;
    }
  }

  return (
    <EditableField
      {...props}
      displayValue={displayValue}
      liveValidate={liveValidate}
      normalizeValue={normalizeValue}
      InputComponent={DynamicInput}
    />
  );
}
